import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { StatsGrid } from "../../ui/Data/StatCard";
import ConversationFilters from "../../ui/Conversations/ConversationFilters";
import ConversationCard from "../../ui/Conversations/ConversationCard";
import { ConversationsGridSkeleton } from "../../ui/Conversations/ConversationSkeletons";
import Pagination from "../../ui/Data/Pagination";
import { useConversations } from "@finitless/shared/hooks";
import { useConversationSocketUpdates } from "../../../hooks/useConversationSocketUpdates";
import { useAiChatsMetrics } from "../../../hooks/useDashboardQueries";
import { PageHeader } from "@finitless/shared/components";
import { BusinessDropdown } from "../../ui/Business/BusinessDropdown";
import { Icon } from "../../ui/UIElements/Icon";
import { formatNumberByLocale } from "../../../lib/localeFormatters";
import MonthYearPicker from "../reports/MonthYearPicker";
import { getCurrentMonth } from "../../../lib/reportUtils";
import { getCreatedAtRangeForFilter } from "@finitless/shared/orderConstants";

const ConversationsPage = () => {
  const { t } = useTranslation("conversations");
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState("all");
  const [statusReasonFilter, setStatusReasonFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [filterBusiness, setFilterBusiness] = useState(null);
  const [isBusinessDropdownOpen, setIsBusinessDropdownOpen] = useState(false);
  const businessDropdownRef = useRef(null);
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const getMonthString = (date) => {
    if (!date) return getCurrentMonth();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
  };

  const ITEMS_PER_PAGE = 15;
  const MAX_VISIBLE_PAGES = 5;

  // Initialize socket updates for real-time conversation updates
  useConversationSocketUpdates();

  // Close business dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        businessDropdownRef.current &&
        !businessDropdownRef.current.contains(event.target)
      ) {
        setIsBusinessDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleBusinessSelect = (brand) => {
    setFilterBusiness(brand);
    setIsBusinessDropdownOpen(false);
    setCurrentPage(1);
  };

  const handleClearBusinessFilter = () => {
    setFilterBusiness(null);
    setCurrentPage(1);
  };

  // Fetch AI chats metrics
  const chatsMetricsParams = { business_id: filterBusiness?.id || null };
  if (selectedDate) {
    chatsMetricsParams.month = getMonthString(selectedDate);
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) {
      chatsMetricsParams.timezone = tz;
    } else {
      chatsMetricsParams.timezone_offset = -(
        new Date().getTimezoneOffset() / 60
      );
    }
  }

  const { data: aiChatsMetricsData, isLoading: metricsLoading } =
    useAiChatsMetrics(chatsMetricsParams);

  // Helper function to format change percentage
  const formatChangePercentage = (change) => {
    if (change === null || change === undefined) return null;
    const isPositive = change >= 0;
    return {
      value: `${isPositive ? "+" : ""}${formatNumberByLocale(change, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })}%`,
      isPositive,
    };
  };

  const createdAtRange = useMemo(
    () => getCreatedAtRangeForFilter(selectedDate),
    [selectedDate],
  );

  // Build query parameters based on filters
  const queryParams = useMemo(() => {
    const params = { page: currentPage };
    const whereConditions = [];

    whereConditions.push(
      {
        attribute: "created_at",
        value: {
          condition: ">=",
          value: createdAtRange.start,
        },
      },
      {
        attribute: "created_at",
        value: {
          condition: "<=",
          value: createdAtRange.end,
        },
      },
    );

    // Only add business_id filter if filterBusiness is set
    if (filterBusiness?.id) {
      whereConditions.push({
        attribute: "business_id",
        value: filterBusiness.id,
      });
    }

    // Add status filter if not 'all'
    if (statusFilter !== "all") {
      whereConditions.push({ attribute: "status", value: statusFilter });
    }

    // Add status_reason filter if not 'all'
    if (statusReasonFilter !== "all") {
      whereConditions.push({
        attribute: "status_reason",
        value: statusReasonFilter,
      });
    }

    params.where = JSON.stringify(whereConditions);
    return params;
  }, [
    statusFilter,
    statusReasonFilter,
    currentPage,
    filterBusiness?.id,
    createdAtRange,
  ]);

  // Fetch conversations
  const {
    data: conversationsData,
    isLoading,
    error,
  } = useConversations(queryParams);

  const conversations = conversationsData?.conversations || [];
  const pagination = conversationsData?.pagination || {};

  // Create metric cards using AI chats metrics data
  const metricCards = useMemo(() => {
    const totalChatsChange = formatChangePercentage(
      aiChatsMetricsData?.total_chats?.change_percentage,
    );
    const completedChatsChange = formatChangePercentage(
      aiChatsMetricsData?.completed_chats?.change_percentage,
    );
    const abandonedChatsChange = formatChangePercentage(
      aiChatsMetricsData?.abandoned_chats?.change_percentage,
    );
    const failedChatsChange = formatChangePercentage(
      aiChatsMetricsData?.failed_chats?.change_percentage,
    );
    const successRateChange = formatChangePercentage(
      aiChatsMetricsData?.success_rate?.change_percentage,
    );

    return [
      {
        id: "ai-total-chats",
        title: t("metrics.totalChats"),
        titleHelp: {
          ariaLabel: t("help.metrics.totalChats.ariaLabel"),
          whatIs: t("help.metrics.totalChats.whatIs"),
          period: t("help.metrics.totalChats.period"),
        },
        value: aiChatsMetricsData?.total_chats?.current ?? 0,
        change: totalChatsChange?.value,
        yesterday: aiChatsMetricsData?.total_chats?.previous ?? 0,
        icon: "MessageCircle",
        iconColor: "text-blue-400",
        iconBg: "bg-blue-500/10",
        gradient: true,
      },
      {
        id: "ai-active-chats",
        title: t("metrics.activeChats"),
        titleHelp: {
          ariaLabel: t("help.metrics.activeChats.ariaLabel"),
          whatIs: t("help.metrics.activeChats.whatIs"),
          period: t("help.metrics.activeChats.period"),
        },
        value: aiChatsMetricsData?.active_chats?.current ?? 0,
        yesterday: aiChatsMetricsData?.active_chats?.previous ?? 0,
        icon: "Clock",
        iconColor: "text-yellow-400",
        iconBg: "bg-yellow-500/10",
        valueColor: "text-yellow-400",
      },
      {
        id: "ai-completed-chats",
        title: t("metrics.completed"),
        titleHelp: {
          ariaLabel: t("help.metrics.completed.ariaLabel"),
          whatIs: t("help.metrics.completed.whatIs"),
          period: t("help.metrics.completed.period"),
        },
        value: aiChatsMetricsData?.completed_chats?.current ?? 0,
        change: completedChatsChange?.value,
        yesterday: aiChatsMetricsData?.completed_chats?.previous ?? 0,
        icon: "CheckCircle",
        iconColor: "text-green-400",
        iconBg: "bg-green-500/10",
        valueColor: "text-green-400",
      },
      {
        id: "ai-abandoned-chats",
        title: t("metrics.abandoned"),
        titleHelp: {
          ariaLabel: t("help.metrics.abandoned.ariaLabel"),
          whatIs: t("help.metrics.abandoned.whatIs"),
          period: t("help.metrics.abandoned.period"),
        },
        value: aiChatsMetricsData?.abandoned_chats?.current ?? 0,
        change: abandonedChatsChange?.value,
        yesterday: aiChatsMetricsData?.abandoned_chats?.previous ?? 0,
        icon: "XCircle",
        iconColor: "text-orange-400",
        iconBg: "bg-orange-500/10",
        valueColor: "text-orange-400",
        changeColor: abandonedChatsChange?.isPositive
          ? "text-red-500"
          : "text-green-500",
      },
      {
        id: "ai-failed-chats",
        title: t("metrics.failed"),
        titleHelp: {
          ariaLabel: t("help.metrics.failed.ariaLabel"),
          whatIs: t("help.metrics.failed.whatIs"),
          period: t("help.metrics.failed.period"),
        },
        value: aiChatsMetricsData?.failed_chats?.current ?? 0,
        change: failedChatsChange?.value,
        yesterday: aiChatsMetricsData?.failed_chats?.previous ?? 0,
        icon: "AlertCircle",
        iconColor: "text-red-400",
        iconBg: "bg-red-500/10",
        valueColor: "text-red-400",
        changeColor: failedChatsChange?.isPositive
          ? "text-red-500"
          : "text-green-500",
      },
      {
        id: "ai-success-rate",
        title: t("metrics.successRate"),
        titleHelp: {
          ariaLabel: t("help.metrics.successRate.ariaLabel"),
          whatIs: t("help.metrics.successRate.whatIs"),
          formula: t("help.metrics.successRate.formula"),
          period: t("help.metrics.successRate.period"),
        },
        value: `${formatNumberByLocale(
          aiChatsMetricsData?.success_rate?.current ?? 0,
          {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          },
        )}%`,
        change: successRateChange?.value,
        yesterday: `${formatNumberByLocale(
          aiChatsMetricsData?.success_rate?.previous ?? 0,
          {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          },
        )}%`,
        icon: "TrendingUp",
        iconColor: "text-purple-400",
        iconBg: "bg-purple-500/10",
        valueColor: "text-purple-400",
      },
    ];
  }, [aiChatsMetricsData]);

  // Calculate pagination data
  const totalPages = pagination?.total_pages || 1;
  const totalItems = pagination?.total || 0;

  // Handle conversation selection
  const handleViewChat = (conversation) => {
    // Navigate to conversation detail page
    navigate(`/analyze/conversations/${conversation.id}`, {
      state: { conversation },
    });
  };

  const handleViewOrder = (orderId) => {
    navigate(`/orders/${orderId}`);
  };

  // Handle filter changes
  const handleStatusFilterChange = (newStatusFilter) => {
    setStatusFilter(newStatusFilter);
    setCurrentPage(1);
  };

  const handleStatusReasonFilterChange = (newStatusReasonFilter) => {
    setStatusReasonFilter(newStatusReasonFilter);
    setCurrentPage(1);
  };

  const handleSearchChange = (term) => {
    setSearchQuery(term);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSelectedDateChange = (date) => {
    setSelectedDate(date);
    setCurrentPage(1);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={
          filterBusiness
            ? t("header.titleWithBusiness", { name: filterBusiness.name })
            : t("header.title")
        }
        subtitle={t("header.subtitle")}
      />

      {/* AI Chats Metrics */}
      <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => handleSelectedDateChange(null)}
              className={`h-10 rounded-lg px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                selectedDate === null
                  ? "bg-blue-600 text-white"
                  : "hover:bg-gray-750 border border-gray-700 bg-gray-800 text-gray-300"
              }`}
            >
              {t("date.today", { ns: "common" })}
            </button>
            <button
              onClick={() => {
                const now = new Date();
                handleSelectedDateChange(
                  new Date(now.getFullYear(), now.getMonth(), 1),
                );
              }}
              className={`h-10 rounded-lg px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                selectedDate &&
                selectedDate.getMonth() === new Date().getMonth() &&
                selectedDate.getFullYear() === new Date().getFullYear()
                  ? "bg-blue-600 text-white"
                  : "hover:bg-gray-750 border border-gray-700 bg-gray-800 text-gray-300"
              }`}
            >
              {t("date.currentMonth", { ns: "common" })}
            </button>
            <button
              onClick={() => {
                const now = new Date();
                handleSelectedDateChange(
                  new Date(now.getFullYear(), now.getMonth() - 1, 1),
                );
              }}
              className={`h-10 rounded-lg px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                selectedDate &&
                selectedDate.getMonth() ===
                  new Date(
                    new Date().getFullYear(),
                    new Date().getMonth() - 1,
                    1,
                  ).getMonth() &&
                selectedDate.getFullYear() ===
                  new Date(
                    new Date().getFullYear(),
                    new Date().getMonth() - 1,
                    1,
                  ).getFullYear()
                  ? "bg-blue-600 text-white"
                  : "hover:bg-gray-750 border border-gray-700 bg-gray-800 text-gray-300"
              }`}
            >
              {t("date.lastMonth", { ns: "common" })}
            </button>
            <MonthYearPicker
              value={selectedDate}
              onChange={handleSelectedDateChange}
              placeholder={t("date.selectMonth", { ns: "common" })}
              id="conversations-month-picker"
            />
          </div>
        </div>
      </div>
      {metricsLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="card animate-pulse p-4 sm:p-6">
              <div className="mb-3 h-3 w-1/2 rounded bg-gray-700 sm:mb-4 sm:h-4"></div>
              <div className="h-6 w-3/4 rounded bg-gray-700 sm:h-8"></div>
            </div>
          ))}
        </div>
      ) : (
        <StatsGrid stats={metricCards} columns={3} />
      )}

      {/* Filters and Search */}
      <div className="mb-6 rounded-lg border border-gray-800 bg-gray-900 p-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          {/* Left side - Business Dropdown and Filters */}
          <div className="flex flex-1 flex-col gap-4 sm:flex-row">
            {/* Business Filter Dropdown */}
            <div className="relative flex-shrink-0" ref={businessDropdownRef}>
              <button
                onClick={() =>
                  setIsBusinessDropdownOpen(!isBusinessDropdownOpen)
                }
                className="hover:bg-gray-750 flex h-10 w-full min-w-[180px] items-center gap-2 rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm transition-colors sm:w-auto"
              >
                <span className="truncate text-gray-300">
                  {filterBusiness
                    ? filterBusiness.name
                    : t("filters.allBusinesses")}
                </span>
                <Icon
                  name={isBusinessDropdownOpen ? "ChevronUp" : "ChevronDown"}
                  className="ml-auto h-4 w-4 flex-shrink-0 text-gray-400"
                />
              </button>
              {filterBusiness && (
                <button
                  onClick={handleClearBusinessFilter}
                  className="absolute -top-2 -right-2 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-red-600 transition-colors hover:bg-red-700"
                  title={t("filters.clearBusiness")}
                >
                  <Icon name="X" className="h-3 w-3 text-white" />
                </button>
              )}
              <div className="absolute top-full left-0 z-50 mt-2">
                <BusinessDropdown
                  isOpen={isBusinessDropdownOpen}
                  onClose={() => setIsBusinessDropdownOpen(false)}
                  selectedBrand={filterBusiness}
                  onBrandSelect={handleBusinessSelect}
                />
              </div>
            </div>

            <ConversationFilters
              statusFilter={statusFilter}
              onStatusFilterChange={handleStatusFilterChange}
              statusReasonFilter={statusReasonFilter}
              onStatusReasonFilterChange={handleStatusReasonFilterChange}
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
              onClearSearch={handleClearSearch}
            />
          </div>
        </div>
      </div>

      {/* Conversations Grid */}
      <div>
        {isLoading ? (
          <ConversationsGridSkeleton />
        ) : error ? (
          <div className="py-12 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-500/10">
              <svg
                className="h-8 w-8 text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h3 className="mb-2 text-lg font-medium text-red-400">
              {t("errors.loadTitle")}
            </h3>
            <p className="text-gray-400">
              {error.message || t("errors.loadDescription")}
            </p>
          </div>
        ) : conversations.length === 0 ? (
          <div className="py-12 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-500/10">
              <svg
                className="h-8 w-8 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
            </div>
            <h3 className="mb-2 text-lg font-medium text-gray-300">
              {searchQuery
                ? t("empty.noConversationsFound")
                : t("empty.noConversationsYet")}
            </h3>
            <p className="text-gray-400">
              {searchQuery ? t("empty.searchHint") : t("empty.defaultHint")}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {conversations.map((conversation) => (
                <ConversationCard
                  key={conversation.id}
                  conversation={conversation}
                  onViewChat={handleViewChat}
                  onViewOrder={handleViewOrder}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8">
                <div className="rounded-lg border border-gray-700 bg-gray-800 p-4">
                  <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
                    {/* Pagination Info */}
                    <div className="text-sm text-gray-400">
                      {t("pagination.showing", {
                        from: (currentPage - 1) * ITEMS_PER_PAGE + 1,
                        to: Math.min(currentPage * ITEMS_PER_PAGE, totalItems),
                        total: totalItems,
                      })}
                    </div>

                    {/* Pagination Controls */}
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      maxVisiblePages={MAX_VISIBLE_PAGES}
                      disabled={isLoading}
                      className="mt-4 sm:mt-0"
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ConversationsPage;
